import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Eye, Loader2, Plus, RotateCcw, Send, Trash2, Upload } from "lucide-react";
import { RevisionReview } from "@/components/admin/RevisionReview";

// V4.6 Admin Technical Knowledge (MEDIA-152; CMD-11-001…006): controlled source
// registration + structured-record governance with draft → validate → publish →
// supersede/retire → rollback lifecycle and full audit history.
const KINDS = ["product_fact", "application_guidance", "evidence_claim", "operational_guidance",
  "installation", "care", "safety", "permission_rule", "open_item"];
const PUB_STATES = { DRAFT: "badge-reserved", PUBLISHED: "badge-available", SUPERSEDED: "badge-committed", RETIRED: "badge-sold" };
const EXT_STATES = ["Internal", "approved", "external with limitation", "approved control rule"];

const EMPTY_DOC = { doc_number: "", title: "", revision: "", issue_date: "", purpose: "", client_facing: false, source_locator: "" };
const EMPTY_REC = { knowledge_id: "", kind: "product_fact", domain: "", topic: "", guidance: "", limitations: "",
  applicability_scope: "", verification_state: "", source_workbook: "ZCKM-001", source_revision: "1.0",
  source_sheet: "", source_row: "", source_document: "", external_publication_status: "Internal",
  dealer_admin: true, location_manager: false, sales_designer: false, color_codes: "", variant_skus: "", effective_date: "" };

const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function AdminKnowledge() {
  const [tab, setTab] = useState("records");
  const [records, setRecords] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [docForm, setDocForm] = useState(null);   // null | EMPTY_DOC
  const [recForm, setRecForm] = useState(null);   // null | EMPTY_REC (create) or edit
  const [editing, setEditing] = useState(null);   // record being edited
  const [sel, setSel] = useState(null);           // selected record + history
  const [validation, setValidation] = useState(null);
  const [rollbackRev, setRollbackRev] = useState("");
  const [busy, setBusy] = useState("");
  // V4.7 COP-TECH-165…171: controlled document files + revision lifecycle
  const [docFile, setDocFile] = useState(null);          // register-dialog file
  const [revForm, setRevForm] = useState(null);          // {doc_number, revision, …, file}
  const [replaceTarget, setReplaceTarget] = useState(null); // {doc_number, revision, file}
  const [retireTarget, setRetireTarget] = useState(null);   // {doc_number, revision}

  const load = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([api.get("/knowledge/admin/records"), api.get("/knowledge/admin/documents")]);
      setRecords(r.data.records || []);
      setDocuments(d.data.documents || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openRecord = async (kid) => {
    try {
      const [{ data: all }, { data: hist }] = await Promise.all([
        api.get("/knowledge/admin/records"), api.get(`/knowledge/admin/records/${kid}/history`)]);
      setSel({ record: all.records.find((x) => x.knowledge_id === kid), history: hist.revisions || [] });
      setValidation(null); setRollbackRev("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const act = async (kind, fn, msg) => {
    setBusy(kind);
    try { await fn(); toast.success(msg); await load(); if (sel) await openRecord(sel.record.knowledge_id); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const registerDocument = () => act("doc", async () => {
    // COP-TECH-165/166: a document is its actual governed file, not a filename entry
    const fd = new FormData();
    Object.entries(docForm).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
    fd.append("file", docFile);
    await api.post("/knowledge/admin/documents/upload", fd);
    setDocForm(null); setDocFile(null);
  }, "Document registered as a draft — publish it to make it current");

  const uploadRevision = () => act("rev", async () => {
    const fd = new FormData();
    fd.append("revision", revForm.revision);
    ["title", "issue_date", "purpose", "source_locator"].forEach((k) => revForm[k] && fd.append(k, revForm[k]));
    if (revForm.client_facing !== null && revForm.client_facing !== undefined) fd.append("client_facing", String(!!revForm.client_facing));
    fd.append("file", revForm.file);
    await api.post(`/knowledge/admin/documents/${revForm.doc_number}/revisions`, fd);
    setRevForm(null);
  }, "Revision uploaded as a draft — publish it to make it current");

  const replaceDocFile = () => act("replace", async () => {
    const fd = new FormData();
    fd.append("revision", replaceTarget.revision);
    fd.append("file", replaceTarget.file);
    await api.post(`/knowledge/admin/documents/${replaceTarget.doc_number}/file`, fd);
    setReplaceTarget(null);
  }, "File replaced — lifecycle state unchanged");

  const docAction = (key, d, path, msg) => act(key, async () => {
    await api.post(`/knowledge/admin/documents/${d.doc_number}/${path}`, { revision: d.revision, note: "Admin Technical Knowledge UI" });
  }, msg);

  const recordBody = (f) => ({
    knowledge_id: f.knowledge_id, kind: f.kind, topic: f.topic,
    domain: f.domain || null, guidance: f.guidance, limitations: f.limitations || null,
    applicability_scope: f.applicability_scope || null, verification_state: f.verification_state || null,
    source_workbook: f.source_workbook, source_revision: f.source_revision,
    source_sheet: f.source_sheet || null, source_row: f.source_row ? Number(f.source_row) : null,
    source_document: f.source_document || null,
    external_publication_status: f.external_publication_status,
    permissions: { dealer_admin: !!f.dealer_admin, location_manager: !!f.location_manager, sales_designer: !!f.sales_designer },
    color_codes: toList(f.color_codes), variant_skus: toList(f.variant_skus),
    effective_date: f.effective_date || null, note: "Admin Technical Knowledge UI",
  });

  const createRecord = () => act("rec", async () => {
    await api.post("/knowledge/admin/records", recordBody(recForm));
    setRecForm(null);
  }, "Draft record created — publish to make it live");

  const saveEdit = () => act("edit", async () => {
    const b = recordBody(recForm);
    delete b.knowledge_id;
    await api.patch(`/knowledge/admin/records/${editing.knowledge_id}`, b);
    setEditing(null); setRecForm(null);
  }, "Draft saved");

  const startEdit = (r) => {
    setEditing(r);
    setRecForm({
      ...EMPTY_REC, knowledge_id: r.knowledge_id, kind: r.kind, topic: r.topic, domain: r.domain || "",
      guidance: r.content?.guidance || "", limitations: r.content?.limitations || "",
      applicability_scope: r.applicability_scope || "", verification_state: r.verification_state || "",
      source_workbook: r.source_workbook || "ZCKM-001", source_revision: r.source_revision || "1.0",
      source_sheet: r.source_sheet || "", source_row: r.source_row || "", source_document: r.source_document || "",
      external_publication_status: r.external_publication_status || "Internal",
      dealer_admin: !!r.permissions?.dealer_admin, location_manager: !!r.permissions?.location_manager,
      sales_designer: !!r.permissions?.sales_designer,
      color_codes: (r.color_codes || []).join(", "), variant_skus: (r.variant_skus || []).join(", "),
      effective_date: r.effective_date || "",
    });
  };

  const RecordForm = ({ form, setForm, onSubmit, submitLabel, isEdit }) => (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {!isEdit && (
          <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Record ID</label>
            <Input value={form.knowledge_id} onChange={(e) => setForm({ ...form, knowledge_id: e.target.value })} className="mt-1 h-10 bg-white font-mono" data-testid="know-field-id" /></div>
        )}
        <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Kind</label>
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger className="mt-1 h-10 bg-white" data-testid="know-field-kind"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{KINDS.map((k) => <SelectItem key={k} value={k}>{k.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Topic</label>
        <Input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="mt-1 h-10 bg-white" data-testid="know-field-topic" /></div>
      <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Guidance (structured content)</label>
        <Textarea rows={3} value={form.guidance} onChange={(e) => setForm({ ...form, guidance: e.target.value })} className="mt-1 bg-white" data-testid="know-field-guidance" /></div>
      <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Limitations / qualifications</label>
        <Textarea rows={2} value={form.limitations} onChange={(e) => setForm({ ...form, limitations: e.target.value })} className="mt-1 bg-white" data-testid="know-field-limitations" /></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Applicability scope</label>
          <Input value={form.applicability_scope} onChange={(e) => setForm({ ...form, applicability_scope: e.target.value })} className="mt-1 h-10 bg-white" data-testid="know-field-scope" /></div>
        <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Verification / evidence status</label>
          <Input value={form.verification_state} onChange={(e) => setForm({ ...form, verification_state: e.target.value })} className="mt-1 h-10 bg-white" data-testid="know-field-verification" /></div>
        <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Source workbook</label>
          <Input value={form.source_workbook} onChange={(e) => setForm({ ...form, source_workbook: e.target.value })} className="mt-1 h-10 bg-white" data-testid="know-field-workbook" /></div>
        <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Source revision</label>
          <Input value={form.source_revision} onChange={(e) => setForm({ ...form, source_revision: e.target.value })} className="mt-1 h-10 bg-white" data-testid="know-field-revision" /></div>
        <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Source sheet</label>
          <Input value={form.source_sheet} onChange={(e) => setForm({ ...form, source_sheet: e.target.value })} className="mt-1 h-10 bg-white" data-testid="know-field-sheet" /></div>
        <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Source row</label>
          <Input value={form.source_row} onChange={(e) => setForm({ ...form, source_row: e.target.value })} className="mt-1 h-10 bg-white" data-testid="know-field-row" /></div>
        <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Registered source document</label>
          <Select value={form.source_document || "none"} onValueChange={(v) => setForm({ ...form, source_document: v === "none" ? "" : v })}>
            <SelectTrigger className="mt-1 h-10 bg-white" data-testid="know-field-document"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="none">— none —</SelectItem>
              {documents.map((d) => <SelectItem key={d.doc_number} value={d.doc_number}>{d.doc_number} Rev {d.revision}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Dealer publication status</label>
          <Select value={form.external_publication_status} onValueChange={(v) => setForm({ ...form, external_publication_status: v })}>
            <SelectTrigger className="mt-1 h-10 bg-white" data-testid="know-field-ext"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{EXT_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Colors (codes, comma-sep)</label>
          <Input value={form.color_codes} onChange={(e) => setForm({ ...form, color_codes: e.target.value })} className="mt-1 h-10 bg-white" data-testid="know-field-colors" /></div>
        <div><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Variants (SKUs, comma-sep)</label>
          <Input value={form.variant_skus} onChange={(e) => setForm({ ...form, variant_skus: e.target.value })} className="mt-1 h-10 bg-white" data-testid="know-field-skus" /></div>
      </div>
      <div className="flex flex-wrap gap-4 rounded-md border border-border bg-sand p-3 text-sm" data-testid="know-permissions">
        {[["dealer_admin", "Dealer Admin"], ["location_manager", "Location Manager"], ["sales_designer", "Sales"]].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2">
            <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} data-testid={`know-perm-${k.replaceAll("_", "-")}`} />{label}
          </label>
        ))}
      </div>
      <Button onClick={onSubmit} disabled={!!busy} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid="know-form-submit">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{submitLabel}
      </Button>
    </div>
  );

  return (
    <div data-testid="admin-knowledge-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Zelligio Admin</p>
      <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Technical Knowledge</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">
        Register controlled source documents and govern structured technical records. Only published records with a
        dealer-visible publication state and authorized role permissions reach the Dealer Technical Library and ZELI.
      </p>

      <div className="mt-6 flex gap-2" data-testid="know-tabs">
        {[["records", "Structured records"], ["documents", "Source documents"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`know-tab-${k}`}
            className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${tab === k ? "border-terracotta bg-terracotta text-white" : "border-border bg-white text-charcoal hover:border-terracotta"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "documents" && (
        <div className="mt-5" data-testid="know-documents">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-warmgrey">
              Each revision carries its actual governed file. Publishing a revision makes it the sole current
              dealer-visible version; prior revisions stay downloadable here as history.
            </p>
            <Button onClick={() => { setDocForm({ ...EMPTY_DOC }); setDocFile(null); }} variant="outline" className="shrink-0 border-terracotta text-terracotta" data-testid="know-register-document">
              <Plus className="mr-2 h-4 w-4" />Upload document
            </Button>
          </div>
          <div className="mt-3 overflow-x-auto rounded-md border border-border bg-white">
            <table className="w-full text-sm" data-testid="know-documents-table">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-3">Doc</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Rev</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">File</th><th className="px-4 py-3">Client-facing</th>
                <th className="px-4 py-3 text-right">Actions</th></tr></thead>
              <tbody>
                {documents.map((d) => (
                  <tr key={`${d.doc_number}-${d.revision}`} className="border-b border-border align-top" data-testid={`know-doc-${d.doc_number}-${d.revision}`}>
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold">{d.doc_number}</td>
                    <td className="px-4 py-2.5">{d.title}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{d.revision}</td>
                    <td className="px-4 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${{ CURRENT: "badge-available", DRAFT: "badge-reserved", SUPERSEDED: "badge-committed", RETIRED: "badge-sold" }[d.status] || "badge-reserved"}`}
                        data-testid={`know-doc-status-${d.doc_number}-${d.revision}`}>{d.status}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs" data-testid={`know-doc-file-${d.doc_number}-${d.revision}`}>
                      {d.has_file ? (
                        <span>{d.file_name || "attached"}{d.file_size ? ` · ${(d.file_size / 1024).toFixed(0)} KB` : ""}</span>
                      ) : (
                        <span className="text-status-sold">No file attached</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs">{d.client_facing ? "Yes" : "No"}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {d.has_file && (
                          <a href={`${api.defaults.baseURL}/knowledge/admin/documents/${encodeURIComponent(d.doc_number)}/file?revision=${encodeURIComponent(d.revision)}`}
                            target="_blank" rel="noreferrer"
                            className="rounded-full border border-limestone px-2.5 py-1 text-[11px] font-medium text-charcoal transition-colors hover:border-terracotta hover:text-terracotta"
                            data-testid={`know-doc-download-${d.doc_number}-${d.revision}`}>
                            <Download className="mr-1 inline h-3 w-3" />Open
                          </a>
                        )}
                        <button type="button" onClick={() => setReplaceTarget({ doc_number: d.doc_number, revision: d.revision, file: null })}
                          className="rounded-full border border-limestone px-2.5 py-1 text-[11px] font-medium text-charcoal transition-colors hover:border-terracotta hover:text-terracotta"
                          data-testid={`know-doc-replace-${d.doc_number}-${d.revision}`}>Replace file</button>
                        {d.status === "CURRENT" && (
                          <button type="button" onClick={() => setRevForm({ doc_number: d.doc_number, revision: "", title: d.title || "", issue_date: d.issue_date || "", purpose: d.purpose || "", source_locator: d.source_locator || "", client_facing: !!d.client_facing, file: null })}
                            className="rounded-full border border-limestone px-2.5 py-1 text-[11px] font-medium text-charcoal transition-colors hover:border-terracotta hover:text-terracotta"
                            data-testid={`know-doc-new-revision-${d.doc_number}`}>New revision</button>
                        )}
                        {(d.status === "DRAFT" || d.status === "RETIRED") && (
                          <button type="button" onClick={() => docAction("publish", d, "publish", `Revision ${d.revision} published — now the current version`)}
                            className="rounded-full border border-terracotta px-2.5 py-1 text-[11px] font-medium text-terracotta transition-colors hover:bg-terracotta hover:text-white"
                            data-testid={`know-doc-publish-${d.doc_number}-${d.revision}`}>Publish</button>
                        )}
                        {d.status === "DRAFT" && (
                          <button type="button" onClick={() => { const reason = window.prompt("Rejection reason (required)"); if (reason) act("reject", async () => { await api.post(`/knowledge/admin/documents/${d.doc_number}/reject`, { revision: d.revision, note: reason }); }, `Revision ${d.revision} rejected — kept as history`); }}
                            className="rounded-full border border-status-sold px-2.5 py-1 text-[11px] font-medium text-status-sold transition-colors hover:bg-status-sold hover:text-white"
                            data-testid={`know-doc-reject-${d.doc_number}-${d.revision}`}>Reject</button>
                        )}
                        {d.status === "CURRENT" && (
                          <button type="button" onClick={() => setRetireTarget({ doc_number: d.doc_number, revision: d.revision })}
                            className="rounded-full border border-status-sold px-2.5 py-1 text-[11px] font-medium text-status-sold transition-colors hover:bg-status-sold hover:text-white"
                            data-testid={`know-doc-retire-${d.doc_number}-${d.revision}`}>Retire</button>
                        )}
                        {(d.status === "SUPERSEDED" || d.status === "RETIRED") && (
                          <button type="button" onClick={() => docAction("rollback", d, "rollback", `Rolled back — revision ${d.revision} is current again`)}
                            className="rounded-full border border-limestone px-2.5 py-1 text-[11px] font-medium text-charcoal transition-colors hover:border-terracotta hover:text-terracotta"
                            data-testid={`know-doc-rollback-${d.doc_number}-${d.revision}`}>Roll back</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <RevisionReview onChanged={load} />
        </div>
      )}

      {tab === "records" && (
        <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]" data-testid="know-records">
          <div>
            <div className="flex justify-end">
              <Button onClick={() => { setRecForm({ ...EMPTY_REC }); setEditing(null); }} variant="outline" className="border-terracotta text-terracotta" data-testid="know-new-record">
                <Plus className="mr-2 h-4 w-4" />New record
              </Button>
            </div>
            <div className="mt-3 max-h-[70vh] overflow-y-auto rounded-md border border-border bg-white">
              {(records || []).map((r) => (
                <button key={r.knowledge_id} type="button" onClick={() => openRecord(r.knowledge_id)}
                  className={`flex w-full items-center justify-between gap-2 border-b border-border px-4 py-2.5 text-left text-sm transition-colors hover:bg-sand ${sel?.record?.knowledge_id === r.knowledge_id ? "bg-sand" : ""}`}
                  data-testid={`know-row-${r.knowledge_id}`}>
                  <span><span className="font-mono text-xs font-semibold">{r.knowledge_id}</span> · {r.topic}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${PUB_STATES[r.publication_status] || "badge-reserved"}`}
                    data-testid={`know-status-${r.knowledge_id}`}>{r.publication_status || "DRAFT"}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            {!sel && !recForm && (
              <div className="flex min-h-[30vh] items-center justify-center rounded-md border border-dashed border-limestone text-sm text-warmgrey" data-testid="know-editor-empty">
                Select a record to govern it, or create a new one.
              </div>
            )}
            {recForm && !editing && (
              <div className="rounded-md border border-border bg-white p-6" data-testid="know-create-form">
                <h2 className="font-display text-2xl font-light text-charcoal">New structured record</h2>
                <div className="mt-4"><RecordForm form={recForm} setForm={setRecForm} onSubmit={createRecord} submitLabel="Create draft" isEdit={false} /></div>
              </div>
            )}
            {recForm && editing && (
              <div className="rounded-md border border-border bg-white p-6" data-testid="know-edit-form">
                <h2 className="font-display text-2xl font-light text-charcoal">Edit draft — {editing.knowledge_id}</h2>
                <div className="mt-4"><RecordForm form={recForm} setForm={setRecForm} onSubmit={saveEdit} submitLabel="Save draft" isEdit /></div>
              </div>
            )}
            {sel && !recForm && (
              <div className="space-y-4 rounded-md border border-border bg-white p-6" data-testid="know-detail">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-2xl font-light text-charcoal" data-testid="know-detail-title">
                    {sel.record.knowledge_id} — {sel.record.topic}
                  </h2>
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase ${PUB_STATES[sel.record.publication_status] || "badge-reserved"}`}
                    data-testid="know-detail-status">{sel.record.publication_status || "DRAFT"} · rev {sel.record.revision || 0}</span>
                </div>
                <p className="text-sm text-charcoal">{sel.record.content?.guidance}</p>
                <p className="font-mono text-[11px] text-muted-foreground">
                  {sel.record.source_workbook} Rev {sel.record.source_revision}
                  {sel.record.source_sheet ? ` · ${sel.record.source_sheet} row ${sel.record.source_row}` : ""}
                  {sel.record.source_document ? ` · ${sel.record.source_document}` : ""}
                </p>
                <div className="flex flex-wrap gap-2 border-t border-border pt-4">
                  <Button onClick={() => startEdit(sel.record)} variant="outline" className="border-limestone" data-testid="know-edit">Edit draft</Button>
                  <Button onClick={() => act("validate", async () => setValidation((await api.post(`/knowledge/admin/records/${sel.record.knowledge_id}/validate`)).data), "Validation complete")}
                    variant="outline" className="border-limestone" data-testid="know-validate"><Eye className="mr-2 h-4 w-4" />Validate</Button>
                  <Button onClick={() => act("publish", () => api.post(`/knowledge/admin/records/${sel.record.knowledge_id}/publish`, { note: "Admin UI" }), "Published — live in Dealer library & ZELI")}
                    disabled={!!busy} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid="know-publish">
                    {busy === "publish" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Publish</Button>
                  <Button onClick={() => act("retire", () => api.post(`/knowledge/admin/records/${sel.record.knowledge_id}/retire`, { note: "Admin UI" }), "Record retired")}
                    disabled={!!busy || sel.record.publication_status === "RETIRED"} variant="outline" className="border-status-sold text-status-sold" data-testid="know-retire">
                    <Trash2 className="mr-2 h-4 w-4" />Retire</Button>
                </div>
                {validation && (
                  <div className="rounded-md border border-border bg-sand p-3 text-sm" data-testid="know-validation">
                    <p className="font-semibold">{validation.valid ? "Valid — publishable" : "Not publishable"}</p>
                    {(validation.errors || []).map((e) => <p key={e} className="mt-1 text-status-sold">{e}</p>)}
                    {(validation.unresolved || []).length > 0 && <p className="mt-1 text-warmgrey">Unresolved: {validation.unresolved.join(", ")}</p>}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={rollbackRev} onValueChange={setRollbackRev}>
                    <SelectTrigger className="h-10 w-40 bg-white" data-testid="know-rollback-select"><SelectValue placeholder="Revision…" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {sel.history.filter((r) => ["publish", "rollback"].includes(r.action)).map((r) => (
                        <SelectItem key={r.revision} value={String(r.revision)}>Revision {r.revision}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => act("rollback", () => api.post(`/knowledge/admin/records/${sel.record.knowledge_id}/rollback`, { revision: Number(rollbackRev), note: "Admin UI" }), `Rolled back to revision ${rollbackRev}`)}
                    disabled={!!busy || !rollbackRev} variant="outline" className="border-limestone" data-testid="know-rollback">
                    <RotateCcw className="mr-2 h-4 w-4" />Roll back</Button>
                </div>
                <div className="border-t border-border pt-3" data-testid="know-history">
                  <p className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Revision &amp; audit history</p>
                  {sel.history.length === 0 ? <p className="mt-2 text-sm text-warmgrey">No publication events yet.</p> : (
                    <ul className="mt-2 space-y-1.5">
                      {sel.history.map((r) => (
                        <li key={`${r.revision}-${r.action}-${r.at}`} className="rounded-md border border-border px-3 py-1.5 text-xs" data-testid={`know-history-${r.revision}-${r.action}`}>
                          <span className="font-mono font-semibold">rev {r.revision}</span> · {r.action} · {r.actor} · {r.at}
                          {r.restored_from ? ` · restored from rev ${r.restored_from}` : ""}
                        </li>))}
                    </ul>)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={!!docForm} onOpenChange={(o) => !o && setDocForm(null)}>
        <DialogContent className="max-w-lg border-border bg-white" data-testid="know-document-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">Upload controlled document</DialogTitle></DialogHeader>
          {docForm && (
            <div className="space-y-3">
              {[["doc_number", "Document number (e.g. MZ-TDS-002)"], ["title", "Title"], ["revision", "Revision"], ["issue_date", "Issue date"], ["purpose", "Purpose"], ["source_locator", "Source locator"]].map(([f, label]) => (
                <div key={f}><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">{label}</label>
                  <Input value={docForm[f]} onChange={(e) => setDocForm({ ...docForm, [f]: e.target.value })} className="mt-1 h-10 bg-white" data-testid={`know-doc-field-${f.replaceAll("_", "-")}`} /></div>
              ))}
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Document file (PDF, Word, Excel…)</label>
                <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => setDocFile(e.target.files?.[0] || null)} className="mt-1 h-10 bg-white" data-testid="know-doc-field-file" />
                <p className="mt-1 text-xs text-warmgrey">The actual governed file is required — a registry entry without its file cannot be published.</p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={docForm.client_facing} onChange={(e) => setDocForm({ ...docForm, client_facing: e.target.checked })} data-testid="know-doc-field-client-facing" />Client-facing (dealer-visible when published)
              </label>
              <Button onClick={registerDocument} disabled={!!busy || !docFile} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid="know-doc-submit">
                {busy === "doc" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<Upload className="mr-2 h-4 w-4" />Upload as draft
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!revForm} onOpenChange={(o) => !o && setRevForm(null)}>
        <DialogContent className="max-w-lg border-border bg-white" data-testid="know-revision-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">New revision — {revForm?.doc_number}</DialogTitle></DialogHeader>
          {revForm && (
            <div className="space-y-3">
              <p className="text-xs text-warmgrey">This adds a draft revision to the existing document. It becomes the current version only when you publish it.</p>
              {[["revision", "New revision (e.g. 2.0)"], ["title", "Title"], ["issue_date", "Issue date"], ["purpose", "Purpose"], ["source_locator", "Source locator"]].map(([f, label]) => (
                <div key={f}><label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">{label}</label>
                  <Input value={revForm[f]} onChange={(e) => setRevForm({ ...revForm, [f]: e.target.value })} className="mt-1 h-10 bg-white" data-testid={`know-rev-field-${f.replaceAll("_", "-")}`} /></div>
              ))}
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Revision file</label>
                <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => setRevForm({ ...revForm, file: e.target.files?.[0] || null })} className="mt-1 h-10 bg-white" data-testid="know-rev-field-file" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!revForm.client_facing} onChange={(e) => setRevForm({ ...revForm, client_facing: e.target.checked })} data-testid="know-rev-field-client-facing" />Client-facing (dealer-visible when published)
              </label>
              <Button onClick={uploadRevision} disabled={!!busy || !revForm.revision.trim() || !revForm.file} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid="know-rev-submit">
                {busy === "rev" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<Upload className="mr-2 h-4 w-4" />Upload revision
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!replaceTarget} onOpenChange={(o) => !o && setReplaceTarget(null)}>
        <DialogContent className="max-w-md border-border bg-white" data-testid="know-replace-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">Replace file — {replaceTarget?.doc_number} rev {replaceTarget?.revision}</DialogTitle></DialogHeader>
          {replaceTarget && (
            <div className="space-y-3">
              <p className="text-xs text-warmgrey">Replaces the file on this revision without changing its status. The replacement is recorded in the audit history.</p>
              <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp"
                onChange={(e) => setReplaceTarget({ ...replaceTarget, file: e.target.files?.[0] || null })} className="h-10 bg-white" data-testid="know-replace-file" />
              <Button onClick={replaceDocFile} disabled={!!busy || !replaceTarget.file} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid="know-replace-submit">
                {busy === "replace" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Replace file
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!retireTarget} onOpenChange={(o) => !o && setRetireTarget(null)}>
        <DialogContent className="max-w-md border-border bg-white" data-testid="know-retire-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">Retire {retireTarget?.doc_number} rev {retireTarget?.revision}?</DialogTitle></DialogHeader>
          {retireTarget && (
            <div className="space-y-3">
              <p className="text-sm text-charcoal">
                Retiring removes this document from the Dealer library and ZELI answers. It is <strong>not deleted</strong>:
                the file, every revision and the full audit history stay available to authorized admins, and you can
                recover it later by publishing a new or prior revision.
              </p>
              <div className="flex gap-2">
                <Button onClick={() => { const t = retireTarget; setRetireTarget(null); docAction("retire", t, "retire", `Revision ${t.revision} retired — file and history preserved`); }}
                  disabled={!!busy} className="bg-status-sold text-white hover:bg-status-sold/90" data-testid="know-retire-confirm">
                  {busy === "retire" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<Trash2 className="mr-2 h-4 w-4" />Retire document
                </Button>
                <Button variant="outline" onClick={() => setRetireTarget(null)} className="border-limestone" data-testid="know-retire-cancel">Keep current</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
